import { ThemeProvider } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import "moment/dist/locale/pl";
import { SnackbarProvider } from "notistack";
import { Suspense, useEffect, useState } from "react";

import WithDesktopOnly from "./hocs/WithDesktopOnly";

import RoleBasedRouterProvider from "./components/RoleBasedRouterProvider";
import { AuthProvider } from "./features/auth/AuthContext";

import { JoyrideProvider } from "./context/JoyrideContext";
import theme from "./utils/theme";

import FullLoadingScreen from "./components/FullLoadingScreen";
import { defaultDomains } from "./constants";
import { CargoTableProvider } from "./context/CargoTableContext";
import { CargosViewSwitcherProvider } from "./context/CargoViewContext";
import { CompanyCargoTableProvider } from "./context/CompanyCargoTableContext";
import { useGetCurrentBrandingQuery } from "./features/branding/brandingApiSlice";
import { useFavicon } from "./hooks/useFavicon";
import { usePageTitle } from "./hooks/usePageTitle";

import { useTranslation } from "react-i18next";
import { useGetSettingsJSONQuery } from "./features/auth/authApiSlice";
import "/node_modules/flag-icons/css/flag-icons.min.css";

// fallback to local env variables
// const tagManagerArgs = {
// 	gtmId: "GTM-MHWQGWXJ",
// 	auth: import.meta.env.GTM_AUTH || "PevnvyRykNR0nwo2WZo4qg",
// 	preview: import.meta.env.GTM_PREVIEW || "env-4",
// };

// TagManager.initialize(tagManagerArgs);

moment.updateLocale("pl", {
	week: {
		dow: 1,
	},
});

function App() {
	const { i18n } = useTranslation();
	const domain = window.location.hostname;

	const [shouldSkip, setShouldSkip] = useState(false);
	const { data, isLoading, error } = useGetCurrentBrandingQuery(undefined, {
		skip: shouldSkip,
	});

	const { isLoading: isLoadingSettings } = useGetSettingsJSONQuery();

	useEffect(() => {
		if (error) {
			setShouldSkip(true);
		}
	}, [error]);

	useFavicon(
		data?.logo
			? data.logo
			: defaultDomains.includes(domain)
				? "/favicon.png"
				: "/favicon-blank.png",
	);
	usePageTitle(
		data?.customerDomain
			? data.customerDomain
			: defaultDomains.includes(domain)
				? "CARGOseller"
				: domain,
	);

	if (isLoading || isLoadingSettings) return <FullLoadingScreen />;

	// fix for invalid branding saves from s-admin
	const filteredData = Object.fromEntries(
		Object.entries(data || {}).filter(
			([_, value]) => value !== "[object Object]",
		),
	);

	const brandedTheme = {
		...theme,
		palette: {
			...theme.palette,
			primary: {
				main: filteredData?.buttonColor || theme.palette.primary.main,
			},
			loginBox: filteredData?.loginBoxColor || theme.palette.loginBox,
			loginBg: filteredData?.loginBgColor || theme.palette.loginBg,
			barColor: filteredData?.barColor || theme.palette.barColor,
		},
	};

	const shouldUseBrandedTheme =
		filteredData?.loginBoxColor ||
		filteredData?.loginBgColor ||
		filteredData?.barColor ||
		filteredData?.buttonColor;

	return (
		<Suspense fallback={<FullLoadingScreen />}>
			<ThemeProvider theme={shouldUseBrandedTheme ? brandedTheme : theme}>
				<LocalizationProvider
					dateAdapter={AdapterMoment}
					adapterLocale={moment.locale(i18n.language || "pl")}
				>
					<SnackbarProvider
						maxSnack={10}
						anchorOrigin={{
							vertical: "top",
							horizontal: "right",
						}}
					>
						<AuthProvider>
							<JoyrideProvider>
								<CargosViewSwitcherProvider>
									<CargoTableProvider>
										<CompanyCargoTableProvider>
											<RoleBasedRouterProvider />
										</CompanyCargoTableProvider>
									</CargoTableProvider>
								</CargosViewSwitcherProvider>
							</JoyrideProvider>
						</AuthProvider>
					</SnackbarProvider>
				</LocalizationProvider>
			</ThemeProvider>
		</Suspense>
	);
}

export default WithDesktopOnly(App);
